import {
	useGetOfferLazyQuery,
	useGetContactLazyQuery,
	useGetProjectLazyQuery,
} from "@repo/server-api";

import { ResultFromData, ResultFromException, useNotificationGraphql } from "@repo/shared/common";

import type { GraphqlQueryResult } from "@repo/shared/common";
import type { Offer } from "../types/CommercialDocumentTypes";
import type { Offer as ServerOffer, Contact, Project } from "@repo/server-api";
import { IdWithName } from "@repo/shared/common";
import { normalizeContact, normalizeProject } from "@repo/legacy/components/helper/Index";

export function useOfferLoader({ onFetched }: { onFetched: (offer: Offer) => void }) {
	const [fetchOffer] = useGetOfferLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [fetchContact] = useGetContactLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [fetchProject] = useGetProjectLazyQuery({
		fetchPolicy: "no-cache",
	});

	const contactGetter = async (id: string): Promise<IdWithName | null> => {
		let contact: IdWithName | null = null;

		contact = await fetchContact({ variables: { id } })
			.then((result) => {
				if (result.data?.node && (result.data.node as Contact).__typename === "Contact") {
					return normalizeContact(result.data.node as Contact);
				}
				return null;
			})
			.catch(() => {
				return null;
			});

		return contact;
	};

	const projectGetter = async (id: string): Promise<IdWithName | null> => {
		let project: IdWithName | null = null;

		project = await fetchProject({ variables: { id } })
			.then((result) => {
				if (
					result.data?.node &&
					(result.data.node as unknown as Project).__typename === "Project"
				) {
					return normalizeProject(result.data.node as unknown as Project);
				}
				return null;
			})
			.catch(() => {
				return null;
			});

		return project;
	};

	const mapOffer = async (node: ServerOffer): Promise<Offer> => {
		const client = node.clientId ? await contactGetter(node.clientId) : null;
		const contractor = node.contractorId ? await contactGetter(node.contractorId) : null;
		const contactPerson = node.clientContactPersonId
			? await contactGetter(node.clientContactPersonId)
			: null;
		const project = node.projectId ? await projectGetter(node.projectId) : null;

		return {
			id: node.id,
			number: node.number ?? "",
			project: project,
			issueDate: node.issueDate ?? "",
			client: client,
			type: node.documentType,
			status: node.status,
			contactPerson: contactPerson,
			contractor: contractor,
			paymentDeadlineDays: node.paymentDeadlineInDays ?? null,
			headerNote: node.introductoryText ?? null,
			footerNote: node.concludingText ?? null,
			items: node.items?.map((item) => ({
				id: item.id,
				name: item.name,
				description: item.description,
				discount: item.discountPercent ?? 0,
				quantity: item.quantity,
				unit: item.unit,
				unitPrice: item.unitPriceNet,
				totalPrice: item.lineNetTotal,
				tax: item.tax,
				taxCode: item.taxCode,
			})),
			dueDate: node.dueDate ?? null,
		};
	};

	const fetchServerData = async (id: string): Promise<GraphqlQueryResult<Offer>> => {
		const result = await fetchOffer({ variables: { id } });

		if (result.error) return ResultFromException<Offer>(result.errors);

		const node = result.data?.node as unknown as ServerOffer;

		if (node?.__typename !== "Offer")
			return ResultFromException<Offer>([
				{
					message: "Offer not found",
				},
			]);

		return ResultFromData<Offer>(await mapOffer(node));
	};

	const { save: fetchData, loading } = useNotificationGraphql<string, Offer>({
		onComplete: (offer: Offer) => {
			onFetched(offer);
		},

		graphqlCall: fetchServerData,
	});

	return { fetchData, loading };
}
